import React from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

// ─── Client-side error reporter ────────────────────────────────────
// Fire-and-forget POST to `/api/errors/client`. Uses `sendBeacon` so
// the request survives a `window.location.reload()` — critical because
// most users will tap "Refresh Page" immediately, and if the report
// happens over a normal `fetch()` the unloading page will cancel it.
//
// Called from three places:
//   1. `ErrorBoundary.componentDidCatch` — React render/lifecycle errors
//   2. `window.onerror`                  — plain sync JS errors
//   3. `window.onunhandledrejection`     — Promise chain rejections
export function reportClientError({ message, stack, componentStack, extra }) {
    try {
        const payload = JSON.stringify({
            message: String(message || 'Unknown error').slice(0, 2000),
            stack: stack ? String(stack).slice(0, 10_000) : null,
            component_stack: componentStack ? String(componentStack).slice(0, 10_000) : null,
            url: typeof window !== 'undefined' ? window.location.href : null,
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            extra: extra || null,
        });
        const url = '/api/errors/client';
        // sendBeacon is the correct primitive here — it's specifically
        // designed for "post analytics on unload" and survives reloads.
        // Fall back to fetch(keepalive) when unavailable.
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            const blob = new Blob([payload], { type: 'application/json' });
            const ok = navigator.sendBeacon(url, blob);
            if (ok) return;
        }
        if (typeof fetch === 'function') {
            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                credentials: 'include',
                keepalive: true,
            }).catch(() => {});
        }
    } catch {
        /* the error-reporter must never itself throw */
    }
}

// One-shot install for global error handlers. Safe to call multiple
// times (guarded by a window-level flag).
export function installGlobalErrorReporters() {
    if (typeof window === 'undefined' || window.__brainsyncErrorReportersInstalled) return;
    window.__brainsyncErrorReportersInstalled = true;
    window.addEventListener('error', (e) => {
        reportClientError({
            message: e?.message || 'window.onerror',
            stack: e?.error?.stack,
            extra: { source: 'window.error', filename: e?.filename, lineno: e?.lineno, colno: e?.colno },
        });
    });
    window.addEventListener('unhandledrejection', (e) => {
        const r = e?.reason;
        reportClientError({
            message: (r && (r.message || String(r))) || 'unhandledrejection',
            stack: r && r.stack,
            extra: { source: 'unhandledrejection' },
        });
    });
}

export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, message: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, message: error?.message || null };
    }

    componentDidCatch(error, info) {
        if (typeof window !== 'undefined' && window.console) {
            // Keep the loud console log for local devtools debugging.
            window.console.error('ErrorBoundary caught:', error, info);
        }
        reportClientError({
            message: error?.message || 'ErrorBoundary',
            stack: error?.stack,
            componentStack: info?.componentStack,
            extra: { source: 'ErrorBoundary' },
        });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#fafaf8' }} data-testid="error-boundary-fallback">
                    <div className="text-center max-w-sm">
                        <div className="w-14 h-14 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: '#fee2e2' }}>
                            <span className="text-2xl" style={{ color: '#ef4444' }}>!</span>
                        </div>
                        <h1 className="font-heading text-xl mb-2" style={{ color: '#1a1a1a' }}>Something went wrong</h1>
                        <p className="text-sm mb-4" style={{ color: '#888' }}>An unexpected error occurred. Please refresh the page to continue.</p>
                        {/* Show the raw error message when we have one — helps
                            technical users file a useful support ticket. Truncated
                            so it never dominates the card. */}
                        {this.state.message && (
                            <p className="text-[11px] font-mono mb-6 px-3 py-2 rounded-lg bg-zinc-100 text-zinc-500 break-all" data-testid="error-boundary-message">
                                {String(this.state.message).slice(0, 200)}
                            </p>
                        )}
                        <Button
                            onClick={() => window.location.reload()}
                            className="rounded-xl px-6"
                            style={{ background: '#10b981', color: 'white' }}
                            data-testid="error-boundary-refresh"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh Page
                        </Button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
